/**
 * How a set / game / frame is won, per sport — a module of its own (no
 * imports) so both the SETS config schema (`results.ts`, a meet's
 * `config.rule`) and the ranking rules (`ranking.ts`) read the same table
 * without importing each other. Re-exported from `ranking.ts`.
 */

/**
 * How a set / game is won: first to `points`, by `winBy` clear points, and —
 * where the sport caps it — whoever reaches `cap` first. `decider` replaces
 * the rule in the deciding set (volleyball's fifth set to 15).
 */
export interface SetRule {
	points: number;
	winBy: number;
	cap?: number | undefined;
	decider?: Omit<SetRule, "decider"> | undefined;
}

/**
 * Set rules the operator screen can pick. Only rules we are sure of; a sport
 * not listed is scored without a rule (any lead wins the set — see `setsWon`).
 *
 *   BADMINTON    21, by 2, capped at 30         — BWF Laws of Badminton, Law 7
 *   TABLE_TENNIS 11, by 2                        — ITTF Handbook, rule 2.11
 *   VOLLEYBALL   25, by 2; deciding set 15, by 2 — FIVB Official Rules 6.2 / 6.3.2
 *   BEACH_VOLLEYBALL 21, by 2; deciding set 15, by 2 — FIVB Beach Volleyball
 *                Official Rules 6.2 / 6.3 (the predecessor's beach screen, too)
 *   SEPAK_TAKRAW 15, by 2, capped at 17 (every set, the third included) — ISTAF
 *                Law of the Game 2024, the 15-point system mandatory since
 *                1 Feb 2024 (ISTAF Executive Committee, 8 Dec 2023): at 14–14
 *                the first to 17 wins. It replaced 21 by 2 to 25 (2013–2024).
 *   TENNIS       a set in GAMES: 6, by 2; at 6–6 a tie-break decides it 7–6,
 *                so 7 is the cap (7–5 and 7–6 both end it) — ITF Rules of
 *                Tennis 5(b). The tie-break's points are `periods[].tiebreak`.
 *   TENNIS_MATCH_TIEBREAK  tennis sets, the DECIDING one replaced by a match
 *                tie-break: first to 10 points by 2, stored as that set's
 *                score (10–8) — ITF Rules, Appendix V (doubles)
 *   PADEL        tennis' games and sets (FIP rules of the game) — its own key
 *                so the format line names it
 *   SQUASH       11, by 2 (point-a-rally) — WSF Rules of Singles Squash, rule 2
 *   TEQBALL      12, by 2 — CONFIRM against the FITEQ Official Rules of
 *                Teqball (the predecessor's results pages played sets to 12)
 *
 * Billiards and snooker have no rule: a frame is won by the higher score
 * (`setsWon` without a rule), pool by racks ("race to", `config.race_to`).
 */
export const SET_RULE_PRESETS = {
	BADMINTON: { points: 21, winBy: 2, cap: 30 },
	TABLE_TENNIS: { points: 11, winBy: 2 },
	VOLLEYBALL: { points: 25, winBy: 2, decider: { points: 15, winBy: 2 } },
	BEACH_VOLLEYBALL: { points: 21, winBy: 2, decider: { points: 15, winBy: 2 } },
	SEPAK_TAKRAW: { points: 15, winBy: 2, cap: 17 },
	TENNIS: { points: 6, winBy: 2, cap: 7 },
	TENNIS_MATCH_TIEBREAK: { points: 6, winBy: 2, cap: 7, decider: { points: 10, winBy: 2 } },
	PADEL: { points: 6, winBy: 2, cap: 7 },
	SQUASH: { points: 11, winBy: 2 },
	TEQBALL: { points: 12, winBy: 2 },
} as const satisfies Record<string, SetRule>;

/** The most tie-break points a set takes as typed (a typo guard; a real tie-break rarely passes 20). */
export const TIEBREAK_POINTS_MAX = 99;

/** A preset's key — what a SETS meet's `config.rule` may name (a tennis doubles rubber's match tie-break). */
export type SetRuleCode = keyof typeof SET_RULE_PRESETS;
export const SET_RULE_CODES = Object.keys(SET_RULE_PRESETS) as [SetRuleCode, ...SetRuleCode[]];

/** The rule a code names; undefined for none or an unknown code. */
export function setRuleOf(code: string | null | undefined): SetRule | undefined {
	return code && Object.hasOwn(SET_RULE_PRESETS, code) ? SET_RULE_PRESETS[code as SetRuleCode] : undefined;
}
